import {DropdownDivider} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useColumnMenuContext} from '../../../../../../context/ColumnMenuContext';
import type {EagColumn} from '../../../../../../types/EagColumn';
import {ColumnMenuFilterTab} from './components/ColumnMenuFilterTab/ColumnMenuFilterTab';
import {ColumnMenuOptionsTab} from './components/ColumnMenuOptionsTab/ColumnMenuOptionsTab';
import {ColumnMenuOptionsTabMobile} from './components/ColumnMenuOptionsTab/ColumnMenuOptionsTabMobile';

type ColumnHeaderMenuProps = {
  column: EagColumn;
  columnPinned: 'LEFT' | 'RIGHT' | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Explicit mobile flag from the caller (HeaderCellActions has already computed
   * it via useIsMobileView() to choose Dropdown vs MobileSheet as the container).
   * Deliberately NOT re-derived here with a second useIsMobileView() call — that
   * would be a second, independent read of the same breakpoint that could in
   * principle disagree with the container the caller already committed to.
   * Defaults to false (desktop) so existing desktop call sites are unaffected.
   */
  isMobile?: boolean;
  /**
   * Viewport-clamped max height in px, measured at open time by the trigger's
   * event handler (see getColumnMenuMaxHeight) — past it the menu scrolls
   * internally instead of running below the viewport bottom (T20-136597).
   * Desktop only; the MobileSheet container manages its own height.
   */
  maxHeight?: number;
};

/**
 * Column header dropdown menu content — a single stacked list, no tabs.
 *
 * ## Layout
 * - Filter section (only when the column has a filter definition): the column's
 *   filter widget rendered inline at the top.
 * - A divider (only when both sections are present).
 * - Options section: sort / pin / autosize / reset actions, rendered as ordinary
 *   dropdown rows below the filter.
 *
 * Tabs were removed — a single scrollable menu (filter on top, actions below) is
 * the conventional column-menu shape and avoids the cramped tab bar in this narrow
 * popover. The active-filter state is already surfaced by the header's blue menu
 * button, so no in-menu indicator is needed.
 *
 * ## Positioning
 * - Desktop (default, isMobile omitted/false): rendered as children of <Dropdown>
 *   from platform/components (in HeaderCellActions' ColumnMenuDropdown). Dropdown
 *   handles portal, positioning, focus management and dismiss.
 * - Mobile (isMobile passed true): rendered as children of <MobileSheet> instead
 *   (HeaderCellActions' ColumnMenuMobileSheet) — no Dropdown/popper in the tree
 *   at all, so its click-outside/positioning logic never runs here. The options
 *   section renders ColumnMenuOptionsTabMobile (plain buttons) rather than
 *   ColumnMenuOptionsTab (DropdownItem), since DropdownItem only fires onClick
 *   inside a real Dropdown ancestor. The filter section is reused as-is on both.
 *
 * ## State / callbacks
 * - Filter state and all action callbacks are read from ColumnMenuContext (no prop drilling)
 */
export function ColumnHeaderMenu(props: ColumnHeaderMenuProps) {
  const columnMenu = useColumnMenuContext();
  const isMobile = props.isMobile === true;

  const hasFilter = isNotNil(props.column.filter);

  return (
    <div
      className={`${isMobile ? 'w-full' : 'min-w-[280px] max-w-[320px]'} ${
        !isMobile && props.maxHeight != null ? 'overflow-y-auto' : ''
      }`}
      style={!isMobile && props.maxHeight != null ? {maxHeight: props.maxHeight} : undefined}
      // stopPropagation: the desktop Dropdown renders this content in a FloatingPortal,
      // but clicks still bubble through the REACT tree into the sortable <th> that
      // mounts the menu — toggling its sort (and reloading the grid) on any click
      // inside the menu (filter widgets, action rows, padding). Same guard as MobileSheet.
      onClick={(event) => event.stopPropagation()}
    >
      <Show when={hasFilter}>
        <>
          <ColumnMenuFilterTab
            column={props.column}
            filterState={columnMenu.filterState}
            onFilterChange={columnMenu.onFilterChange}
          />
          <DropdownDivider />
        </>
      </Show>

      {isMobile ? (
        <ColumnMenuOptionsTabMobile
          currentPinned={props.columnPinned}
          columnKey={props.column.key}
          columnSortable={props.column.sortable === true}
          onClose={props.onClose}
        />
      ) : (
        <ColumnMenuOptionsTab
          currentPinned={props.columnPinned}
          columnKey={props.column.key}
          columnSortable={props.column.sortable === true}
          onClose={props.onClose}
        />
      )}
    </div>
  );
}
